import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { projectDetails } from '../../../../../../../core/projects.selectors';
import { Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { takeUntil, map } from 'rxjs/operators';

@Component({
    selector: 'app-forms-summary',
    templateUrl: './forms-summary.component.html',
    styleUrls: ['./forms-summary.component.scss'],
    standalone: false
})
export class FormsSummaryComponent implements OnInit, OnDestroy {
  /** Lets a superseded link's badge add "· map now rev M" on the read-only card. */
  readonly projectId$ = inject(Store).select(projectDetails).pipe(map(project => project?.id ?? null));
  @Input() data: any;
  matchedId: any;
  matchedNo: any;
  widgetIds: any = [];
  displayMatched: boolean;
  private readonly onDestroy: Subject<any> = new Subject<any>();
  private readonly SUMMARY_SEPARATOR = ' · ';

  // Summary lines for the card, derived from the control form's
  // ui_config.summary_display (mobile parity): line 1 is the configured header
  // field values, line 2 is per-section line counts (e.g. "Labor 2 · Equipment 1").
  get summaryLines(): string[] {
    const controlForm = this.data?.control_form;
    const summaryDisplay = controlForm?.ui_config?.summary_display || {};
    const fieldNames: string[] = summaryDisplay?.fields || [];
    const sectionNames: string[] = summaryDisplay?.section_counts || [];
    if (!fieldNames.length && !sectionNames.length) {
      return [];
    }

    const headerFields = controlForm?.ui_config?.header?.fields || [];
    const fieldValues = this.data?.field_values || [];

    const fieldsText = fieldNames
      .map((fieldName) => {
        const field = headerFields.find((f: any) => f?.name === fieldName);
        if (!field?.id) {
          return '';
        }
        const fv = fieldValues.find((v: any) => v?.id === field.id);
        return this.formatSummaryValue(fv?.value);
      })
      .filter(Boolean)
      .join(this.SUMMARY_SEPARATOR);

    const lineFields = controlForm?.ui_config?.lines?.fields || [];
    const lines = this.data?.lines || [];

    const sectionCountsText = sectionNames
      .map((sectionName) => {
        const sectionFieldIds = lineFields
          .filter((field: any) => field?.ui_config?.section_name === sectionName)
          .map((field: any) => field.id);
        if (!sectionFieldIds.length) {
          return '';
        }
        const count = lines.filter((line: any) =>
          (line?.field_values || []).some(
            (fv: any) => sectionFieldIds.includes(fv?.id) && !!this.formatSummaryValue(fv?.value),
          ),
        ).length;
        return count > 0 ? `${sectionName} ${count}` : '';
      })
      .filter(Boolean)
      .join(this.SUMMARY_SEPARATOR);

    return [fieldsText, sectionCountsText].filter(Boolean);
  }

  private formatSummaryValue(value: any): string {
    if (Array.isArray(value)) {
      return value.map((v) => this.formatSummaryValue(v)).filter(Boolean).join(', ');
    }
    if (value && typeof value === 'object') {
      return this.formatSummaryValue(value?.name ?? value?.value ?? value?.date ?? value?.label);
    }
    if (value === null || value === undefined) {
      return '';
    }
    return `${value}`.trim();
  }

  constructor(
    private route: ActivatedRoute,
    private cdRef: ChangeDetectorRef,
  ) {
  }

  ngOnInit() {
    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp && qp['matchedId']) {
          this.matchedId = qp['matchedId'];

          if (!Array.isArray(this.matchedId)) {
            this.matchedId = [this.matchedId];
          }

          if (this.matchedId?.length) {
            this.matchedId?.forEach((item) => {
              this.widgetIds?.push(item?.split('_')?.[1]);
            });
          }

          const tempData = this.matchedId?.filter((item) => {
            const parts = item?.split('_');
            return parts?.length === 3 && parts[1] === this.data?.id;
          });
          this.matchedNo = tempData[0]?.split('_')?.[2];
        }

        this.setDisplayMatched();
      });
  }

  setDisplayMatched() {
    if (this.data?.id && this.widgetIds?.length) {
      if (this.widgetIds?.includes(this.data?.id)) {
        this.displayMatched = true;
      }
    }

    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
